/**
 * googleSignIn — fetches the Google OAuth URL with retry on transient
 * backend failures, then navigates the browser to Google.
 *
 * Why not just `window.location.href = '/api/auth/google/login?...'`?
 * Because if the backend container is momentarily unavailable during a
 * deploy or pod replacement, the user lands on nginx's raw 502 page
 * with no way back. By fetching the URL via JSON first, we can retry
 * silently for ~3s before giving up, and surface a friendly toast on
 * total failure instead of an nginx page.
 *
 * Honours the same query params as `/api/auth/google/login`:
 *   - return_url    : where to send the user after callback
 *   - invite_token  : invite-flow handoff
 *
 * Usage:
 *   import { googleSignIn } from "@/lib/googleSignIn";
 *   await googleSignIn({ returnUrl: "/profile" });
 */
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const RETRIES = 3;
const RETRY_DELAY_MS = 600; // 600 → 1200 → 1800 ≈ 3.6s total wall time

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const googleSignIn = async ({ returnUrl, inviteToken } = {}) => {
  const params = new URLSearchParams();
  if (returnUrl) params.set("return_url", returnUrl);
  if (inviteToken) params.set("invite_token", inviteToken);
  const qs = params.toString();
  const endpoint = `${API}/api/auth/google/login-url${qs ? `?${qs}` : ""}`;

  let lastError = null;
  for (let attempt = 0; attempt < RETRIES; attempt++) {
    try {
      const res = await fetch(endpoint, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });
      // Retry only on 5xx — 4xx means the request itself is wrong, no point retrying.
      if (res.status >= 500 && res.status < 600) {
        lastError = new Error(`HTTP ${res.status}`);
        if (attempt < RETRIES - 1) {
          await sleep(RETRY_DELAY_MS * (attempt + 1));
          continue;
        }
        break;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data?.url) throw new Error("No URL in response");
      window.location.href = data.url;
      return;
    } catch (err) {
      lastError = err;
      if (attempt < RETRIES - 1) {
        await sleep(RETRY_DELAY_MS * (attempt + 1));
        continue;
      }
    }
  }
  // Final fallback: try the legacy redirect endpoint directly. If THAT
  // works, the user signs in normally; if it 502s too, they at least
  // get the same page they would have anyway.
  try {
    toast.error("Sign-in is taking longer than usual. Retrying…");
  } catch { /* toast not available — silent */ }
  // eslint-disable-next-line no-console
  console.warn("[googleSignIn] all retries failed", lastError);
  window.location.href = `${API}/api/auth/google/login${qs ? `?${qs}` : ""}`;
};
